/**
 * Gives a member the speaker role, optionally on a named event.
 *
 * Replaces a navigation that pretended to be an action: the members table's
 * "Add speaker profile" used to redirect to the speakers page with the member's
 * openId in the query string, where nothing was waiting to receive it — so the
 * administrator arrived at a roster that did not contain the person they had
 * just tried to add, and nothing had been written.
 *
 * Speaker is a role layered onto a member, never a record of its own, so this
 * takes a member and grants the role. Every field on the speaker record comes
 * from the account, which is what keeps the two from disagreeing.
 */
import { useEffect, useState } from "react";
import { Loader2, Mic2, X } from "lucide-react";
import { chiNotify } from "@/components/ChiNotification";
import { trpc } from "@/lib/trpc";
import { crmLabelStyle } from "@/components/crmFormStyles";
import EventSelect from "@/components/EventSelect";

export default function AddSpeakerRoleDialog({
  member,
  onClose,
  onPromoted,
}: {
  member: { name: string | null; email: string | null };
  onClose: () => void;
  onPromoted?: () => void;
}) {
  const [eventSlug, setEventSlug] = useState("");
  const [visible, setVisible] = useState(false);
  const utils = trpc.useUtils();

  const displayName = member.name ?? member.email ?? "this member";


  const promote = trpc.speakers.admin.promoteMemberToSpeaker.useMutation({
    onSuccess: async result => {
      await Promise.all([
        utils.speakers.admin.list.invalidate(),
        utils.members.adminRecords.invalidate(),
      ]);
      const where = result.eventTitle ? ` on ${result.eventTitle}` : "";
      chiNotify.success(
        result.alreadyHadRole ? `${displayName} updated` : `${displayName} is now a speaker`,
        {
          description: result.notified
            ? `Approved${where}. A congratulations email is on its way.`
            : `Approved${where}. They were already approved, so no email was re-sent.`,
        },
      );
      onPromoted?.();
      onClose();
    },
    onError: error => chiNotify.error("Could not add the speaker role", { description: error.message }),
  });

  useEffect(() => {
    setVisible(true);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !promote.isPending) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [promote.isPending, onClose]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (promote.isPending || !member.email) return;
    promote.mutate({ email: member.email, eventSlug: eventSlug || undefined });
  };

  return (
    <>
      <div
        role="presentation"
        onClick={() => { if (!promote.isPending) onClose(); }}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.65)", backdropFilter: "blur(4px)", zIndex: 999, opacity: visible ? 1 : 0, transition: "opacity .25s ease" }}
      />

      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-speaker-role-title"
        aria-busy={promote.isPending}
        onSubmit={submit}
        style={{
          position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: "min(520px, calc(100vw - 2rem))", background: "#0D1220", zIndex: 1000,
          border: "1px solid rgba(233,30,140,.28)", borderRadius: "1rem",
          boxShadow: "0 30px 90px rgba(0,0,0,.6)", padding: "1.5rem",
          opacity: visible ? 1 : 0, transition: "opacity .25s ease",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", marginBottom: "1rem" }}>
          <div>
            <h2 id="add-speaker-role-title" style={{ margin: 0, display: "flex", alignItems: "center", gap: ".5rem", fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.35rem", color: "white", letterSpacing: ".04em" }}>
              <Mic2 size={16} style={{ color: "#E91E8C" }} /> Add speaker to event
            </h2>
            <p style={{ margin: ".25rem 0 0", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
              {displayName}{member.email ? ` · ${member.email}` : ""}
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={promote.isPending} aria-label="Close" style={{ background: "none", border: "none", color: "#64748B", cursor: promote.isPending ? "not-allowed" : "pointer", padding: 0 }}>
            <X size={17} />
          </button>
        </div>

        <label htmlFor="speaker-role-event" style={crmLabelStyle}>Event</label>
        <EventSelect
          id="speaker-role-event"
          value={eventSlug}
          onChange={next => setEventSlug(next ?? "")}
          noneLabel="No event yet — just grant the speaker role"
        />

        <p style={{ margin: ".85rem 0 0", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>
          They move to the Speakers page as an approved speaker and are emailed a
          congratulations{eventSlug ? " summarising the event, with a calendar invite" : ""}. Their name,
          company, title and photo come from their member account, so nothing is
          re-typed. Someone who is already an approved speaker is assigned the event
          without a second email.
        </p>

        {!member.email && (
          <p role="alert" style={{ margin: ".85rem 0 0", color: "#FCA5A5", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)" }}>
            This member has no email address, which a speaker record is keyed on.
          </p>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: ".6rem", marginTop: "1.25rem" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={promote.isPending}
            style={{ padding: ".6rem 1rem", borderRadius: ".5rem", border: "1px solid rgba(255,255,255,.12)", background: "transparent", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 600, cursor: "pointer" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={promote.isPending || !member.email}
            style={{
              display: "flex", alignItems: "center", gap: ".4rem", padding: ".6rem 1.1rem", borderRadius: ".5rem", border: "none",
              background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "white",
              fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700,
              cursor: promote.isPending || !member.email ? "not-allowed" : "pointer",
              opacity: promote.isPending || !member.email ? .55 : 1,
            }}
          >
            {promote.isPending ? <Loader2 className="animate-spin" size={13} /> : <Mic2 size={13} />}
            {promote.isPending ? "Adding…" : "Add speaker"}
          </button>
        </div>
      </form>
    </>
  );
}
